import { useGtm, type DataLayerObject } from '@gtm-support/vue-gtm';
import { ref } from 'vue';

/**
 * Object used for tracking
 */
interface PushObject {
    event: string;
    event_name: string;
    eventCategory: string;
    eventAction: string;
    eventLabel: string;
    eventNonInteraction: number;
    interaction_type: string;
    click_text?: string; // example_value= 'Events' OR 'Sustainability' OR
    // 'Rebate Redemption' OR 'About Miele' OR 'Miele Professional'
    name_friendly?: string;
    click_url?: string; // '{{name_friendly}}', //example= 'Search' OR 'Dealer Search' OR 'Cart' OR ' Account'
    navigation_level?: string; // only for header
}

/**
 * Enum that contains the different navigation level
 */
enum NavigationLevel {
    primary = 'primary',
    secondary = 'secondary',
    tertiary = 'tertiary',
}

enum EventActionsHeader {
    logo = 'Miele Logo',
    primary = 'Primary',
    secondary = 'Secondary',
    tertiary = 'Tertiary',
    secondaryNonProduct = 'Secondary_Non-Product',
}

enum EventActionsFooter {
    social = 'Social',
    footerLinks = 'Footer Links',
    contact = 'Contact',
    legal = 'Legal',
    experienceCenter = 'Flagship Stores',
}

export function useTracking() {
    /**
     * Function that pushs data with a predefined pushobject for the header
     * @param useNameFriendly Boolean to determine if name_friendly or click_text will be used
     * @param eventAction The action of the event
     * @param eventLabel The label of the event. Format will be '{{click_text}} | {{click_url}}'
     *  or ''(empty string) due to possible data breach issues
     * @param click_url The url that got clicked
     * @param navigation_level The navigation level, values can be 'primary', 'secondary', 'tertiary'
     * @param click_text The label of the link
     * @param name_friendly The label of the link, but manually set to prevent data breach issues
     */

    const isGtmEnabled = ref<boolean>(false);
    function setEnabled(isEnabled: boolean) {
        isGtmEnabled.value = isEnabled;
    }

    function doHeaderDataLayerPush(
        useNameFriendly: boolean,
        eventAction: string,
        eventLabel: string,
        click_url?: string,
        click_text?: string,
        name_friendly?: string
    ) {
        const pushObject: PushObject = {
            event: 'MieleBasic_Interaction', // does not change
            event_name: 'header_interaction', // does not change
            eventCategory: 'Navigation - Header', // does not change
            eventAction,
            eventLabel,
            eventNonInteraction: 0,
            interaction_type: 'header_click', // does not change
        };

        if (useNameFriendly) {
            pushObject.name_friendly = name_friendly || '';
        } else {
            pushObject.click_text = click_text || '';
            pushObject.click_url = click_url || '';
        }

        const dataLayer = getDataLayer();

        dataLayer?.push(pushObject);
    }

    /**
     * Function that pushs data with a predefined pushobject for the footer
     * @param eventAction The action of the event
     * @param eventLabel The label of the event. Format will be '{{click_text}} | {{click_url}}'
     *  or just '{{click_text}}' for links with contact data
     * @param click_url The url that got clicked
     * @param click_text The label of the link
     * @param name_friendly (optional)
     */
    function doFooterDataLayerPush(
        eventAction: string,
        eventLabel: string,
        click_url: string,
        click_text: string,
        name_friendly?: string
    ) {
        const pushObject: PushObject = {
            event: 'MieleBasic_Interaction', // does not change
            event_name: 'footer_interaction', // does not change
            eventCategory: 'Navigation - Footer', // does not change
            eventAction,
            eventLabel,
            eventNonInteraction: 0, // does not change
            interaction_type: 'footer_click', //does not change
            click_text: click_text,
            click_url,
        };

        if (name_friendly) {
            pushObject.name_friendly = name_friendly;
        }

        const dataLayer = getDataLayer();

        dataLayer?.push(pushObject);
    }

    // Helper functions

    function getDataLayer(): DataLayerObject[] {
        const gtm = useGtm();
        if (gtm?.dataLayer()) return gtm?.dataLayer() as DataLayerObject[];
        if (!window?.dataLayer) window.dataLayer = [];

        return window?.dataLayer;
    }

    return {
        setEnabled,
        NavigationLevel,
        EventActionsHeader,
        EventActionsFooter,
        doHeaderDataLayerPush,
        doFooterDataLayerPush,
    };
}
